import Component from '@glimmer/component';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { VStack } from 'tio-ui/components/layout';
import PageHero from 'tio-ui/components/page-hero';
import SectionHeader from 'tio-ui/components/section-header';
import FilterTabs from 'tio-ui/components/filter-tabs';
import type { FilterTab } from 'tio-ui/components/filter-tabs';
import Card from 'tio-ui/components/card';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import { PhChartLineUp, PhListChecks } from 'ember-phosphor-icons';
import type RouterService from '@ember/routing/router-service';
import type IntlService from 'ember-intl/services/intl';

const ROUTE_PREFIX = 'authenticated.questions-you-have.repayment-plans';

export default class RepaymentPlansPage extends Component {
  @service declare router: RouterService;
  @service declare intl: IntlService;

  get tabs(): FilterTab[] {
    return [
      {
        id: 'standard',
        label: this.intl.t('questions_you_have.repayment_plans.standard.tab_title'),
      },
      {
        id: 'graduated',
        label: this.intl.t('questions_you_have.repayment_plans.graduated.tab_title'),
      },
      {
        id: 'extended',
        label: this.intl.t('questions_you_have.repayment_plans.extended.tab_title'),
      },
      {
        id: 'income-driven',
        label: this.intl.t('questions_you_have.repayment_plans.income_driven.tab_title'),
      },
    ];
  }

  get activeTab(): string {
    const name = this.router.currentRouteName ?? '';
    if (name === `${ROUTE_PREFIX}.graduated`) return 'graduated';
    if (name === `${ROUTE_PREFIX}.extended`) return 'extended';
    if (name === `${ROUTE_PREFIX}.income-driven`) return 'income-driven';
    return 'standard';
  }

  setTab = (id: string) => {
    const route = id === 'standard' ? `${ROUTE_PREFIX}.index` : `${ROUTE_PREFIX}.${id}`;
    this.router.transitionTo(route);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  <template>
    <VStack class="gap-8 pb-12">
      <PageHero
        @icon={{PhChartLineUp}}
        @title={{t "questions_you_have.repayment_plans.what_are_repayments_q"}}
        @subtitle={{t "questions_you_have.repayment_plans.what_are_repayments_a"}}
        @gradient="brand"
        @decoration="circles"
        @rounded="2xl"
      />

      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card @padding="lg">
          <Heading @level={{2}} @size="lg" class="mb-2">
            {{t "questions_you_have.repayment_plans.most_people_standard_title"}}
          </Heading>
          <Text @tag="p" @variant="muted">
            {{t "questions_you_have.repayment_plans.most_people_standard_description"}}
          </Text>
        </Card>
        <Card @padding="lg">
          <Heading @level={{2}} @size="lg" class="mb-2">
            {{t "questions_you_have.repayment_plans.payment_too_high_title"}}
          </Heading>
          <Text @tag="p" @variant="muted">
            {{t "questions_you_have.repayment_plans.payment_too_high_description"}}
          </Text>
        </Card>
        <Card @padding="lg">
          <Heading @level={{2}} @size="lg" class="mb-2">
            {{t "questions_you_have.repayment_plans.eligibility_should_be_considered_title"}}
          </Heading>
          <Text @tag="p" @variant="muted">
            {{t
              "questions_you_have.repayment_plans.eligibility_should_be_considered_description"
              htmlSafe=true
            }}
          </Text>
        </Card>
      </div>

      <div>
        <SectionHeader
          @icon={{PhListChecks}}
          @title={{t "questions_you_have.repayment_plans.repayment_plan_types"}}
          @iconClass="tio-fg-brand"
        />
        <FilterTabs
          @tabs={{this.tabs}}
          @activeId={{this.activeTab}}
          @onSelect={{this.setTab}}
          @activeClass="tio-filter-tab-active--indigo"
          @showCounts={{false}}
          class="mb-6"
        />
        {{outlet}}
      </div>
    </VStack>
  </template>
}
